// src/features/auth/pages/ReferralPage.tsx
import { useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AuthLogo } from "../components/AuthLogo";
import { AuthButton } from "../components/AuthButton";
import { EncryptedText } from "../../../components/ui/animated/EncriptedText";
import ReferralInput from "../components/ReferralInput";
import ReferralQuestion from "../components/ReferralQuestion";
import RewardsList from "../components/RewardsList";
import { AnimatedArrow } from "../../../components/ui/animated/AnimatedArrow";
import { useReferral } from "../hooks/use-referral";
import { useArrowAnimation } from "../../../hooks/use-arrow-animation";
import type { ReferralPageProps } from "../auth.types";
import referallBg from "../assets/images/referall-background.webp";
import logoImg from "../assets/images/logo.webp";

// ── Framer variants ────────────────────────────────────────────────────────

const pageVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      duration: 0.55,
      ease: "easeOut",
      staggerChildren: 0.09,
      delayChildren: 0.05,
    },
  },
} as const;

const itemVariants = {
  hidden: { opacity: 0, y: 18 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, ease: [0.25, 0.46, 0.45, 0.94] },
  },
} as const;

const railVariants = {
  hidden: { scaleY: 0, opacity: 0 },
  show: {
    scaleY: 1,
    opacity: 1,
    transition: { duration: 0.6, ease: "easeOut", delay: 0.05 },
  },
} as const;

const dividerVariants = {
  hidden: { scaleX: 0, opacity: 0 },
  show: {
    scaleX: 1,
    opacity: 1,
    transition: { duration: 0.4, ease: "easeOut", delay: 0.02 },
  },
} as const;

// ──────────────────────────────────────────────────────────────────────────

export const ReferralPage = ({
  // Content
  title = "Referral",
  description = "Lorem Ipsum is simply dummy text of the printing and typesetting industry.",
  referralQuestion = "Ai fost adus de cineva de pe server?",
  referralDescription = "Daca introduci codul de referal al cuiva, vei beneficia de recompensele de mai jos.",
  inputPlaceholder = "TYPE HERE",
  // Button labels
  submitLabel = "Logare",
  onContinue = () => {},

  // Auth state
}: ReferralPageProps) => {
  const inputWrapperRef = useRef<HTMLDivElement>(null);

  const {
    code,
    error,
    rewards,
    isLoading,
    isSuccess,
    handleChange,
    handleSubmit,
    handleKeyDown,
  } = useReferral({}); //  onSuccess onError - optional

  const { isGuidanceActive, triggerGuidance, resetGuidance } =
    useArrowAnimation({ autoTriggerDelay: 800, duration: 3200 });

  const isAuthenticated = false;

  // When user is not autnheitcated
  useEffect(() => {
    if (!isAuthenticated) {
      // Implement logic here
    }
  }, [isAuthenticated]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    resetGuidance();
    handleChange(e);
  };

  return (
    <div
      className="relative min-h-screen w-full overflow-hidden chakra-petch-font"
      aria-label="Referral page"
    >
      {/* ── Background ──────────────────────────────────────────────────── */}
      <motion.div
        className="absolute inset-0 bg-cover bg-right bg-no-repeat"
        style={{ backgroundImage: `url(${referallBg})` }}
        initial={{ opacity: 0, scale: 1.04 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.9, ease: [0.25, 0.46, 0.45, 0.94] }}
        aria-hidden="true"
      />

      {/* ── Vignette ────────────────────────────────────────────────────── */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 65% 80% at 28% 50%, transparent 30%, rgba(0,0,0,0.55) 100%)",
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.1, delay: 0.2 }}
        aria-hidden="true"
      />

      {/* ── Guidance blur overlay ────────────────────────────────────────── */}
      <motion.div
        className="absolute inset-0 pointer-events-none z-20"
        animate={
          isGuidanceActive
            ? {
                backdropFilter: "blur(3px)",
                backgroundColor: "rgba(0,0,0,0.38)",
              }
            : { backdropFilter: "blur(0px)", backgroundColor: "rgba(0,0,0,0)" }
        }
        transition={{ duration: 0.4, ease: "easeInOut" }}
        aria-hidden="true"
      />

      {/* ── Layout ──────────────────────────────────────────────────────── */}
      <div className="min-h-screen flex items-center">
        <main className="relative z-30 flex pl-[130px]">
          <motion.div
            className="flex flex-col justify-center max-w-[420px] w-full"
            variants={pageVariants}
            initial="hidden"
            animate="show"
          >
            {/* ── Left rail ─────────────────────────────────────────────── */}
            <motion.div
              className="w-[1px] h-full absolute top-0 bottom-0 left-[65px] bg-[#D9D9D9]/10 flex flex-col justify-between"
              variants={railVariants}
              style={{ transformOrigin: "top" }}
              aria-hidden="true"
            >
              <div className="w-full h-[8px] bg-white" />
              <div className="w-full h-[8px] bg-white" />
            </motion.div>

            {/* ── Logo ──────────────────────────────────────────────────── */}
            <motion.div variants={itemVariants}>
              <AuthLogo src={logoImg} alt="Atlantis Roleplay logo" />
            </motion.div>

            {/* ── Title + description ───────────────────────────────────── */}
            <motion.div
              className="mt-10 mb-6 flex flex-col gap-3"
              variants={itemVariants}
            >
              <h1
                className="text-white text-[65px] uppercase tracking-wide bebas-neue-font leading-[0.85]"
                style={{
                  textShadow: "0 2px 4px rgba(255,255,255,0.6)",
                  fontWeight: "bold",
                }}
              >
                <EncryptedText text={title} flipDelayMs={5} />
              </h1>
              <p className="text-[#FFFFFF]/50 text-sm leading-relaxed">
                <EncryptedText text={description} revealDelayMs={10} />
              </p>
            </motion.div>

            {/* ── Referral question ─────────────────────────────────────── */}
            <motion.div variants={itemVariants}>
              <ReferralQuestion
                label={referralQuestion}
                onClick={triggerGuidance}
              />
            </motion.div>

            {/* ── Divider ───────────────────────────────────────────────── */}
            <motion.div
              className="mt-5 mb-4 h-[2px] w-[80px] bg-gradient-to-r from-[#1a6bff] to-transparent rounded-full"
              variants={dividerVariants}
              style={{ transformOrigin: "left" }}
              aria-hidden="true"
            />

            {/* ── Referral description ──────────────────────────────────── */}
            <motion.p
              className="text-[#FFFFFF]/70 text-md leading-[1.3] mb-5"
              variants={itemVariants}
            >
              {referralDescription}
            </motion.p>

            {/* ── Input + guidance arrow ────────────────────────────────── */}
            <motion.div className="mb-5 relative" variants={itemVariants}>
              <AnimatedArrow
                visible={isGuidanceActive}
                direction="right"
                size="md"
                variant="glow"
                color="#48A7FF"
                label={code ? "Your code" : "ENTER CODE"}
                delay={0.12}
                className="absolute z-40"
                style={{
                  top: "50%",
                  left: "-110px",
                  transform: "translateY(-50%)",
                }}
              />

              {/* Input highlight ring */}
              <motion.div
                className="absolute inset-0 rounded-xl pointer-events-none z-40"
                animate={
                  isGuidanceActive
                    ? {
                        boxShadow: [
                          "0 0 0 0px rgba(72,167,255,0)",
                          "0 0 0 3px rgba(72,167,255,0.7)",
                          "0 0 0 3px rgba(72,167,255,0.45)",
                        ],
                      }
                    : { boxShadow: "0 0 0 0px rgba(72,167,255,0)" }
                }
                transition={
                  isGuidanceActive
                    ? { duration: 0.9, times: [0, 0.35, 1], ease: "easeOut" }
                    : { duration: 0.3 }
                }
                aria-hidden="true"
              />

              <div ref={inputWrapperRef}>
                <ReferralInput
                  value={code}
                  placeholder={inputPlaceholder}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  error={error ?? undefined}
                  success={isSuccess}
                  disabled={isLoading || isSuccess}
                />
              </div>
            </motion.div>

            {/* ── Rewards list ──────────────────────────────────────────── */}
            <RewardsList rewards={rewards} isLoading={isLoading} />

            {/* ── CTA Button ────────────────────────────────────────────── */}
            {/*
              BEFORE success → submit button (validate code)
              AFTER success  → continue button (onContinue fires, e.g. navigate to /login)
              If onContinue is not passed, continue button is hidden (assume parent handles it)
            */}
            <motion.div variants={itemVariants}>
              <AnimatePresence mode="wait">
                {isSuccess && onContinue ? (
                  <motion.div
                    key="continue"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.3 }}
                  >
                    <AuthButton
                      label={"Continue"}
                      type="button"
                      fullWidth
                      onClick={onContinue}
                    />
                  </motion.div>
                ) : (
                  <motion.div
                    key="submit"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.3 }}
                  >
                    <AuthButton
                      label={isSuccess ? "Cod aplicat ✓" : submitLabel}
                      type="button"
                      fullWidth
                      onClick={handleSubmit}
                      isLoading={isLoading}
                      disabled={isLoading || isSuccess}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </motion.div>
        </main>
      </div>
    </div>
  );
};
